import { handleActions } from 'redux-actions';
import {
  loginSuccess,
  loginFailure,
  loginUnavailable,
  forgotPasswordRequest,
  forgotPasswordFailure,
  loginRequest,
  changePasswordRequest,
  changePasswordFailure,
  changePasswordSuccess,
  clearValidationErrors,
  getProfileData,
  setCurrentCode,
  setCurrentPhone,
  addResend, 
  setPhone,
  resetResendCount,
  twoFactorError,
  twoFactorRejected,
  twoFactorUnavailable,
  setCurrentBloodbank,
  setToAllBloodbanks,
  setPasswords,
  userLogoutRequest,
  updateToken,
  setIsInTwoFactor,
  finalizeLoginRequest,
  finalizeLoginSuccess,
  finalizeLoginFailure,
  setCurrentEmail,
  setAuthSessionId,
  setLoginLoading,
  setResendLoading,
  setFirewallError,
  setFeatures,
} from './actions';
import { LOGIN_FAILURE_KIND } from './loginFailureKind';

const initialState = {
  idToken: null,
  expTimestamp: null,
  fetching: false,
  allBloodbanks: [],
  currentBloodbank: '',
  bloodbankName: '',
  loginErrors: false,
  // 'credentials' | 'unavailable' | null — which copy LoginForm renders
  // for loginErrors (see loginFailureKind.js).
  loginErrorKind: null,
  loginLoading: false,
  resendLoading: false,
  firewallError: null,
  finalizeLoginLoading: false,
  forgotPasswordError: null,
  changePasswordErrors: {
    oldPasswordError: null,
    newPasswordError: null,
  },
  imageRef: null,
  roleName: null,
  currentCode: null,
  phone: null,
  currentPhone: null,
  userId: null,
  resendCount: 0,
  isTwoFactorError: false,
  twoFactorErrorKind: null,
  passwords: {
    oldPassword: null,
    newPassword: null,
  },
  isInTwoFactor: false,
  currentEmail: null,
  authSessionId: null,
  // Staff feature flags ({ reports: true, ... }) from the admins doc —
  // nav/route gating only, never authorization (constants/features.js).
  features: {},
};

const authReducer = handleActions({
  [loginRequest]: (state) => ({
    ...state,
    loginErrors: false,
    loginErrorKind: null,
    firewallError: null,
  }),
  [updateToken]:(state, action) => ({
    ...state,
    idToken: action.payload,
  }),
  [loginSuccess]: (state, action) => ({
    ...state,
    resendCount: 0,
    idToken: action.payload.token,
    expTimestamp: action.payload.claims.exp * 1000,
    // Authorization comes from the token claims only — keep roleName in
    // sync on every token (re)fetch; never fall back to any other source.
    roleName: action.payload.claims.role || null,
    isTwoFactorError: false,
  twoFactorErrorKind: null,
  }),
  [loginFailure]: (state, action) => ({
    ...state,
    idToken: null,
    expTimestamp: null,
    loginErrors: true,
    loginErrorKind: LOGIN_FAILURE_KIND.CREDENTIALS,
  }),
  // Same effect on the session as loginFailure; only the copy differs
  // ("the service is unavailable", not "wrong username or password").
  [loginUnavailable]: (state) => ({
    ...state,
    idToken: null,
    expTimestamp: null,
    loginErrors: true,
    loginErrorKind: LOGIN_FAILURE_KIND.UNAVAILABLE,
  }),
  [finalizeLoginRequest]: (state) => ({
    ...state,
    finalizeLoginLoading: true,
  }),
  [finalizeLoginSuccess]: (state) => ({
    ...state,
    finalizeLoginLoading: false,
  }),
  [finalizeLoginFailure]: (state) => ({
    ...state,
    finalizeLoginLoading: false,
    loginErrors: true,
  }),
  [forgotPasswordRequest]: (state) => ({
    ...state,
    forgotPasswordError: null,
  }),
  [forgotPasswordFailure]: (state, action) => ({
    ...state,
    forgotPasswordError: action.payload,
  }),
  [changePasswordRequest]: (state) => ({
    ...state,
    fetching: true,
    changePasswordErrors: { oldPassword: null, newPassword: null, },
  }),
  [changePasswordSuccess]: (state) => ({
    ...state,
    fetching: false,
  }),
  [changePasswordFailure]: (state, action) => ({
    ...state,
    fetching: false,
    changePasswordErrors: { ...state.changePasswordErrors, ...action.payload },
  }),
  [clearValidationErrors]: (state) => ({
    ...state,
    loginErrors: false,
    loginErrorKind: null,
    firewallError: null,
    changePasswordErrors: { oldPassword: null, newPassword: null, },
    forgotPasswordError: null,
    loginLoading: false,
  }),
  [getProfileData]: (state, action) => ({
    ...state,
    imageRef: action.payload.profileStorageRef,
    roleName: action.payload.roleName,
    phone: action.payload.phone,
    userId: action.payload.uid
  }),
  [setCurrentCode]: (state, action) => ({
    ...state,
    currentCode: action.payload,
    isTwoFactorError: false,
  twoFactorErrorKind: null,
  }),
  [setCurrentPhone]: (state, action) => ({
    ...state,
    currentPhone: action.payload,
    isTwoFactorError: false,
  twoFactorErrorKind: null,
  }),
  [addResend]: (state) => ({
    ...state,
    resendCount: parseInt(state.resendCount + 1)
  }),
  [setPhone]: (state, action) => ({
    ...state,
    currentPhone: action.payload,
    isTwoFactorError: false,
  twoFactorErrorKind: null,
  }),
  [resetResendCount]: (state) => ({
    ...state,
    resendCount: 0,
    isTwoFactorError: false,
  twoFactorErrorKind: null,
  }),
  [twoFactorError]: (state) => ({
    ...state,
    isTwoFactorError: true,
    twoFactorErrorKind: 'code',
  }),
  [twoFactorRejected]: (state) => ({
    ...state,
    isTwoFactorError: true,
    twoFactorErrorKind: 'rejected',
  }),
  [twoFactorUnavailable]: (state) => ({
    ...state,
    isTwoFactorError: true,
    twoFactorErrorKind: 'unavailable',
  }),
  [setCurrentBloodbank]: (state, action) => ({
    ...state,
    currentBloodbank: action.payload?.bloodbankId,
    bloodbankName: action.payload?.bloodbankName,
  }),
  [setToAllBloodbanks]: (state, action) => ({
    ...state,
    allBloodbanks: [...action.payload],
  }),
  [setPasswords]: (state, action) => ({
    ...state,
    passwords: action.payload,
  }),
  [userLogoutRequest]: () => ({
    ...initialState
  }),
  [setIsInTwoFactor]: (state, action)=> ({
    ...state,
    isInTwoFactor: action.payload
  }),
  [setCurrentEmail]: (state, action) => ({
    ...state,
    currentEmail: action.payload
  }),
  [setAuthSessionId]: (state, action) => ({
    ...state,
    authSessionId: action.payload,
  }),
  [setLoginLoading]: (state, action) => ({
    ...state,
    loginLoading: action.payload,
  }),
  [setResendLoading]: (state, action) => ({
    ...state,
    resendLoading: action.payload,
  }),
  [setFirewallError]: (state, action) => ({
    ...state,
    firewallError: action.payload,
  }),
  [setFeatures]: (state, action) => ({
    ...state,
    features: action.payload || {},
  }),
}, initialState);

export default authReducer;
